import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { Permission } from '@frontend/authorization';
import { ClassType, EntityType, Locale } from '@frontend/common';
import { HorizontalButtonGroupButton } from '@frontend/components/common';
import { ConfirmationModal } from '@frontend/components/modal';
import { TablePlaceholder } from '@frontend/elements';
import { Report } from '@frontend/report/types';
import { DefaultListValues, resetSelect } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { ColumnDef } from '@tanstack/react-table';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { RowElement } from '../element/row-element.component';
import { FilterOptions, HeaderFilter } from '../header/header-filter';
import { InsaneFilter } from '../insane-filter/insane-filter.component';
import { ObjectColumnsProps } from '../object-column-mapper';
import { useObjectListLayout } from '../object-list-layout/object-list-layout.context';
import { Table, TableProps } from '../table/table';
import { ObjectListActionButtons } from './object-list-action-buttons';
import { ObjectListLoadingTable } from './object-list-loading-table';
import useObjectList from './object-list.controller';

export interface ObjectListProps<T extends { id: string }> {
    listStoreIdCallback?: (id: string) => void;
    label?: React.ReactNode;
    fetch: (arg?: ApiQueryParams<DefaultQueryParams | string | number>) => Promise<PaginatedResponse<T>>;
    dispatch: ThunkDispatch<any, any, Action>;
    eventListener?: EntityType;
    id?: string;
    /**
     * @default false
     * @description set to true if the list should be taken out of the cache (only works when passing a unique id)
     */
    useStaticListId?: boolean;
    columns: Omit<ObjectColumnsProps<T>, 'dispatch'>;
    multiSelectOptions?: MultiSelectOptions<T>;
    onSelectElement?: (object: T) => void;
    sortingColumns?: string[];
    /**
     * @description Columns where the sort direction should be reversed when sent to the backend.
     * This is useful for computed values where ascending/descending needs to be inverted.
     */
    reverseSortingColumns?: string[];
    create?: {
        label?: React.ReactNode;
        onCreate: () => void;
        icon?: HorizontalButtonGroupButton['icon'];
        requiredPermissions?: Permission[];
        overwriteCreateLabel?: React.ReactNode;
    };
    includeDeleted?: {
        requiredPermissions: Permission[];
    };
    type: EntityType;
    filter?: FilterOptions;
    activeFilter?: {
        searchKeys?: string[];
        filterKeys?: string[];
        extrtaProps?: Map<string, object>;
        displayName?: Map<string, React.ReactNode>;
        queryKeyMap?: Map<string, string>;
        export?: {
            overwriteExport?: () => void;
            modalId?: string;
            report?: Report;
            data?: { [key: string]: any };
            dataKeys?: string[];
        };
    };
    selectedObjects?: (obj: T[]) => void;
    defaultListValues?: DefaultListValues;

    cardClassName?: string;
    cardHeaderClassName?: string;
    cardBodyClassName?: string;
    rowClassName?: string;
    customDivclassName?: string;
    customLabelClassName?: string;
    customButtons?: HorizontalButtonGroupButton[];
    customHeaderComponent?: React.ReactNode;
    tableProps?: { [K in keyof TableProps]?: TableProps[K] };
    hideColumns?: string[];

    showCount?: boolean;
    locale?: Locale;
}

export interface MultiSelectOptions<T> {
    delete?: {
        enabled: (obj: T[]) => boolean;
        delete: (ids: string[]) => Promise<void>;
        label?: React.ReactNode;
        icon?: HorizontalButtonGroupButton['icon'];
        parent?: React.ReactNode;
        requiredPermissions?: Permission[];
    };
    custom?: HorizontalButtonGroupButton[];
}

export const ObjectList = <T extends { id: string }>(props: ObjectListProps<T>) => {
    const viewProps = useObjectList<T>(props);
    const location = useLocation();
    const listLayout = useObjectListLayout();
    const isInitialLoading = viewProps.isLoading && !viewProps.isUpdating;
    const usesAdminLayout = listLayout === 'admin' || location.pathname.includes('/admin');

    return (
        <>
            <div
                id={props.id}
                className={
                    'bg-background-surface border-border-default rounded-2xl border ' + (props.cardClassName !== undefined ? props.cardClassName : 'w-full')
                }>
                {usesAdminLayout ? (
                    <div
                        id='admin-table-header'
                        className={props.cardHeaderClassName !== undefined ? props.cardHeaderClassName : 'inline-flex w-full items-center justify-between p-4'}>
                        <div className='flex w-full flex-col items-start md:flex-row md:content-start md:items-center md:justify-between'>
                            <div className='size- order-1 mb-2 inline-flex items-center justify-start gap-3 md:mb-0 md:w-1/3'>
                                {props.filter && (
                                    <HeaderFilter<T>
                                        dispatch={props.dispatch}
                                        filter={props.filter}
                                        {...viewProps}
                                    />
                                )}
                            </div>
                            <h4 className='text-brand-primary order-1 m-0 ms-2 mb-2 text-2xl font-semibold md:order-2 md:mb-0 md:w-1/3 md:text-center'>
                                {props.label}
                            </h4>
                            {props.customHeaderComponent && props.customHeaderComponent}
                            <div className='order-2 mb-2 md:order-3 md:mb-0 md:w-1/3'>
                                <ObjectListActionButtons<T>
                                    {...props}
                                    {...viewProps}
                                    onMultiDelete={() =>
                                        viewProps.openModal(viewProps.multiDeleteModalId, () => (
                                            <ConfirmationModal
                                                handleClose={() => viewProps.closeModal(viewProps.multiDeleteModalId)}
                                                message={
                                                    props.multiSelectOptions?.delete?.parent
                                                        ? parentMultiDeleteMessage(
                                                              viewProps.selectedObjects.length,
                                                              props.type,
                                                              props.multiSelectOptions.delete.parent
                                                          )
                                                        : listMultiDeleteMessage(viewProps.selectedObjects.length, props.type)
                                                }
                                                onConfirm={() => {
                                                    props
                                                        .multiSelectOptions!.delete!.delete(viewProps.selectedObjects.map((o) => o.id))
                                                        .then(() => {
                                                            if (viewProps.listId) {
                                                                props.dispatch(resetSelect({ listId: viewProps.listId }));
                                                            }
                                                            viewProps.changeSelectedObjects([]);
                                                        })
                                                        .then(() => viewProps.closeModal(viewProps.multiDeleteModalId));
                                                }}
                                                severity={ClassType.DANGER}
                                                show={true}
                                                cascadeWarning
                                            />
                                        ))
                                    }
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className='flex flex-col'>
                        {(props.label || props.customHeaderComponent) && (
                            <div className='inline-flex items-center justify-between self-stretch'>
                                <div
                                    className={
                                        props.customLabelClassName
                                            ? props.customLabelClassName
                                            : 'inline-flex items-start justify-start gap-2.5 self-stretch px-4 pt-4'
                                    }>
                                    <div className='text-foreground-default justify-start text-xl font-semibold'>{props.label}</div>
                                </div>
                                {props.customHeaderComponent && props.customHeaderComponent}
                            </div>
                        )}
                        {props.activeFilter && (
                            <InsaneFilter
                                dispatch={props.dispatch}
                                listId={viewProps.listId}
                                {...props.activeFilter}
                            />
                        )}
                    </div>
                )}
                <div
                    aria-busy={viewProps.isUpdating}
                    className='pointer-events-none h-0.5 w-full overflow-hidden'>
                    {viewProps.isUpdating && <div className='bg-brand-primary h-full w-full animate-pulse' />}
                </div>
                <div className='card-body mx-0 mb-0 px-0 pt-0 pb-0'>
                    {viewProps.error ? (
                        <div className='flex flex-col items-center justify-center p-8 text-center'>
                            <p className='text-brand-muted text-lg'>
                                <FormattedMessage
                                    id='ObjectList.error.data-unavailable'
                                    defaultMessage='Data is unavailable'
                                />
                            </p>
                        </div>
                    ) : isInitialLoading ? (
                        <ObjectListLoadingTable columns={viewProps.columns.length} />
                    ) : viewProps.data ? (
                        <Table
                            dispatch={props.dispatch}
                            listId={viewProps.listId}
                            useStaticListId={props.useStaticListId}
                            customDivclassName={props.customDivclassName}
                            key={props.id}
                            id={props.id + '-table'}
                            columns={viewProps.columns as ColumnDef<any>[]}
                            RowElement={RowElement}
                            data={viewProps.data}
                            selectEnabled={!!props.multiSelectOptions}
                            onSelectElement={props.onSelectElement}
                            rowClassName={props.rowClassName}
                            sortingColumns={props.sortingColumns}
                            reverseSortingColumns={props.reverseSortingColumns}
                            hideColumns={props.hideColumns}
                            showCount={props.showCount}
                            optionsModalOpen={viewProps.optionsModalOpen}
                            setOptionsModalOpen={viewProps.setOptionsModalOpen}
                            {...props.tableProps}
                        />
                    ) : (
                        <TablePlaceholder />
                    )}
                </div>
            </div>
        </>
    );
};

const listMultiDeleteMessage = (count: number, type: EntityType) => {
    return (
        <FormattedMessage
            id='ObjectList.multi-delete-modal.message'
            defaultMessage='Are you sure you want to remove {count} {type}s?'
            values={{ count, type: type.replace(/_/g, ' ') }}
        />
    );
};

const parentMultiDeleteMessage = (count: number, type: EntityType, parent: React.ReactNode) => {
    return (
        <FormattedMessage
            id='ObjectList.multi-delete-modal.message_with_parent'
            defaultMessage='Are you sure you want to remove {count} {type}s from this {parent}?'
            values={{ count, type: type.replace(/_/g, ' '), parent }}
        />
    );
};
